import type { Board } from "./board";
import { Direction, directionDelta } from "./direction";
import { XY } from "./xy";

const allDeltas = Object.values(Direction).map((d) => directionDelta(d));

// Sentinel meaning "not set" — distances are always >= 0.
const UNSET = -1;

export class DistanceMap {
	private readonly width: number;
	private readonly height: number;
	private readonly data: Int32Array;

	constructor(width: number, height: number) {
		this.width = width;
		this.height = height;
		this.data = new Int32Array(width * height).fill(UNSET);
	}

	private indexOf(x: number, y: number): number {
		return y * this.width + x;
	}

	private inBounds(x: number, y: number): boolean {
		return x >= 0 && x < this.width && y >= 0 && y < this.height;
	}

	set(xy: XY, distance: number): void {
		this.data[this.indexOf(xy.x, xy.y)] = distance;
	}

	get(xy: XY): number | undefined {
		if (!this.inBounds(xy.x, xy.y)) return undefined;
		const v = this.data[this.indexOf(xy.x, xy.y)];
		return v === UNSET ? undefined : v;
	}

	has(xy: XY): boolean {
		if (!this.inBounds(xy.x, xy.y)) return false;
		return this.data[this.indexOf(xy.x, xy.y)] !== UNSET;
	}

	forEach(callback: (xy: XY, distance: number) => void): void {
		for (let y = 0; y < this.height; y++) {
			const rowStart = y * this.width;
			for (let x = 0; x < this.width; x++) {
				const v = this.data[rowStart + x];
				if (v !== UNSET) callback(new XY(x, y), v);
			}
		}
	}

	/**
	 * BFS flood-fill matching the original Java BoardUtil.calculateDistanceMap.
	 * Diagonal steps cost 2, cardinal steps cost 1.
	 *
	 * Uses typed arrays for the frontier queue (ring buffer with O(1) dequeue)
	 * and visited/frontier-membership bitmaps, indexed by `y * width + x`.
	 */
	static buildFrom(board: Board, origin: XY, isPassable: (xy: XY) => boolean): DistanceMap {
		const width = board.width;
		const height = board.height;
		const cellCount = width * height;
		const dm = new DistanceMap(width, height);

		// Ring buffer for the frontier queue — stores packed indices.
		// Sized for worst case (every cell queued at most once).
		const frontier = new Int32Array(cellCount);
		let head = 0;
		let tail = 0;

		// Bitmaps for O(1) membership checks.
		const frontierSet = new Uint8Array(cellCount);
		const visited = new Uint8Array(cellCount);

		const originIdx = dm.indexOf(origin.x, origin.y);
		frontier[tail++] = originIdx;
		frontierSet[originIdx] = 1;
		dm.data[originIdx] = 0;

		while (head < tail) {
			const currentIdx = frontier[head++];
			frontierSet[currentIdx] = 0;
			if (visited[currentIdx]) continue;
			visited[currentIdx] = 1;

			const currentX = currentIdx % width;
			const currentY = (currentIdx - currentX) / width;

			let lowestNeighborValue = UNSET;
			let lowestNeighborX = 0;
			let lowestNeighborY = 0;

			// Visit each of the 8 compass-direction neighbors of the current cell.
			for (const delta of allDeltas) {
				const neighborX = currentX + delta.x;
				const neighborY = currentY + delta.y;
				if (!dm.inBounds(neighborX, neighborY)) continue;
				const neighborIdx = dm.indexOf(neighborX, neighborY);

				if (visited[neighborIdx]) {
					// Already-visited neighbor: candidate for computing our own distance.
					const neighborValue = dm.data[neighborIdx];
					if (lowestNeighborValue === UNSET || neighborValue < lowestNeighborValue) {
						lowestNeighborValue = neighborValue;
						lowestNeighborX = neighborX;
						lowestNeighborY = neighborY;
					}
					continue;
				}

				// Unvisited neighbor: enqueue it if passable and not already queued.
				// Only allocate an XY when we actually need to consult isPassable.
				if (!isPassable(new XY(neighborX, neighborY))) continue;
				if (frontierSet[neighborIdx]) continue;
				frontier[tail++] = neighborIdx;
				frontierSet[neighborIdx] = 1;
			}

			if (lowestNeighborValue === UNSET) {
				dm.data[currentIdx] = 0;
			} else {
				const diagonal = lowestNeighborX !== currentX && lowestNeighborY !== currentY;
				dm.data[currentIdx] = lowestNeighborValue + (diagonal ? 2 : 1);
			}
		}

		return dm;
	}

	/**
	 * From start, follow the gradient downhill toward the origin.
	 * Returns the list of XY steps (excluding start).
	 * Matches the original Java DistanceMap.rollDownhill.
	 */
	rollDownhill(start: XY): XY[] {
		let current: XY | null = start;
		const pointsList: XY[] = [];
		let currentValue = this.get(current);

		while (current !== null) {
			let next: XY | null = null;
			for (const delta of allDeltas) {
				const neighbor: XY = current.add(delta);
				const neighborValue = this.get(neighbor);
				if (neighborValue === undefined) continue;
				if (currentValue === undefined || neighborValue < currentValue) {
					currentValue = neighborValue;
					next = neighbor;
				}
			}
			if (next !== null) pointsList.push(next);
			current = next;
		}

		return pointsList;
	}
}
